import { z } from "zod"

/**
 * Zod schemas shared as the input contract for each Blitz RPC mutation/query.
 *
 * Each schema is used in three roles:
 * - Server-side validation of the untrusted payload via `resolver.zod(Schema)` — the
 *   authoritative trust boundary; the resolver body never runs on invalid input.
 * - The TypeScript type for that payload via `z.infer<typeof Schema>`, so the shape
 *   flowing client -> server is defined once.
 * - Client-side form validation (Formik `validateZodSchema` / the `schema` prop) for UX.
 *
 * Client validation is best-effort; the server `resolver.zod` check is the real gate.
 */

export const CreateScenario = z.object({
  name: z.string().nonempty().max(512),
  city: z.string().nonempty().max(512),
})

export const RenameScenario = z.object({
  name: z.string().nonempty().max(512),
})

// Treat a blank field as 0 trees, but keep the rest of the
// contract strict: anything non-blank must be a genuine integer.
const emptyToZero = (v: unknown) => (v === "" || (typeof v === "string" && v.trim() === "") ? 0 : v)

const treeCount = z.preprocess(emptyToZero, z.number().int())

const TreesBase = z.object({
  treesSmall: treeCount,
  treesMedium: treeCount,
  treesLarge: treeCount,
})

/**
 * Payload for the updateAreaOnScenario mutation: it plants an area (tree counts + label) and, in
 * the same atomic call, seeds/writes the containing BlockgroupOnScenario's target.
 * `treeEquityScoreTarget` is required.
 */
export const UpdateAreaOnScenario = TreesBase.extend({
  // Parcel/right-of-way label; null = no custom name, UI derives one from area.type on read
  name: z.string().nullable(),
  scenarioId: z.number().int(),
  areaId: z.string(),
  // The containing blockgroup's target.
  treeEquityScoreTarget: z.number(),
})

export const CreateAreaOnScenario = TreesBase.extend({
  scenarioId: z.number().int(),
  areaId: z.string(),
})

export const DeleteAreaOnScenario = z.object({
  scenarioId: z.number().int(),
  areaId: z.string(),
})

// Rename-only payload: changes an AreaOnScenario's label and nothing else. Renaming has no
// bearing on the planting or the containing blockgroup's target, so it is its own mutation
// (renameAreaOnScenario) rather than an overload of updateAreaOnScenario.
export const RenameAreaOnScenario = z.object({
  scenarioId: z.number().int(),
  areaId: z.string(),
  // Parcel/right-of-way label; null = no custom name, UI derives one from area.type on read.
  name: z.string().nullable(),
})

export const UpdateBlockgroupOnScenario = z.object({
  // Optional: omit to let the server seed the locale default on create; present = rename.
  name: z.string().optional(),
  treeEquityScoreTarget: z.number(),
  scenarioId: z.number().int(),
  blockgroupId: z.string(),
  trees: z.number().int().optional(),
})

export const RenameScenarioBlockgroup = z.object({
  name: z.string(),
  treeEquityScoreTarget: z.number(),
  scenarioId: z.number().int(),
  blockgroupId: z.string(),
})

export const DeleteBlockgroupOnScenario = z.object({
  scenarioId: z.number().int(),
  blockgroupId: z.string(),
})
